import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Validate token expiration helper
const isTokenValid = (token) => {
  if (!token) return false;
  
  try {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return tokenPayload.exp > currentTime;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

// Get user from localStorage and validate token
const storedUserInfo = JSON.parse(localStorage.getItem('userInfo'));
let userInfo = null;

// Check if this is a new browser session (tab was closed and reopened)
const sessionId = sessionStorage.getItem('sessionId');
const storedSessionId = localStorage.getItem('sessionId');

// Only use stored user info if:
// 1. Token is still valid
// 2. Session ID matches (same browser tab session)
if (storedUserInfo && 
    storedUserInfo.token && 
    isTokenValid(storedUserInfo.token) && 
    sessionId && 
    sessionId === storedSessionId) {
  userInfo = storedUserInfo;
} else {
  // Clear invalid/expired session data or new browser session
  localStorage.removeItem('userInfo');
  localStorage.removeItem('branch');
  localStorage.removeItem('sessionId');
  sessionStorage.removeItem('sessionId');
}

const initialState = {
  userInfo: userInfo,
  loading: false,
  error: null,
};

// Login user
export const login = createAsyncThunk('auth/login', async (userData, thunkAPI) => {
  try {
    const { data } = await axios.post('/api/users/login', userData);
    
    // Handle OTP requirement response
    if (data.requiresOTP) {
      return data; // Return OTP requirement response
    }
    
    // Store branch selection in local storage for successful login
    if (userData.branch) {
      localStorage.setItem('branch', userData.branch);
    }
    
    return data;
  } catch (error) {
    let message = 'Login failed. Please try again.';
    let statusCode = error.response?.status;
    
    if (error.response && error.response.data && error.response.data.message) {
      message = error.response.data.message;
    } else if (error.message) {
      message = error.message;
    }
    
    // Add status code to error for frontend handling
    const errorData = {
      message,
      statusCode,
      isAccountLocked: statusCode === 423
    };
    
    return thunkAPI.rejectWithValue(errorData);
  }
});

// Update user profile
export const updateProfile = createAsyncThunk('auth/updateProfile', async (profileData, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put('/api/users/profile', profileData, config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Update password
export const updatePassword = createAsyncThunk('auth/updatePassword', async (passwordData, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put('/api/users/password', passwordData, config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.userInfo = null;
      state.error = null;
      localStorage.removeItem('userInfo');
      localStorage.removeItem('branch');
      localStorage.removeItem('sessionId');
      sessionStorage.removeItem('sessionId');
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        
        // Handle OTP requirement response
        if (action.payload.requiresOTP) {
          // Don't set userInfo for OTP requirement
          state.error = null;
          return;
        }
        
        // Successful login - set user info
        state.userInfo = action.payload;
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        
        // Generate unique session ID for browser tab closure detection
        const sessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('sessionId', sessionId);
        localStorage.setItem('sessionId', sessionId);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.userInfo = null;
      })
      .addCase(updatePassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        
        // Maintain existing session ID after password update
        const existingSessionId = sessionStorage.getItem('sessionId');
        if (existingSessionId) {
          localStorage.setItem('sessionId', existingSessionId);
        }
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.userInfo = action.payload;
        localStorage.setItem('userInfo', JSON.stringify(action.payload));
        
        // Maintain existing session ID after profile update
        const existingSessionId = sessionStorage.getItem('sessionId');
        if (existingSessionId) {
          localStorage.setItem('sessionId', existingSessionId);
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;

